import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Upload } from 'lucide-react';
import clsx from 'clsx';

import {
  FONT_FILE_ACCEPT,
  SYSTEM_FONTS,
  addCustomFont,
  getFontLabel,
  getFontStack,
  useBrandFonts,
  useCustomFonts,
} from '../../config';

import scss from './TextToolbar.module.scss';

function FontPicker({ value, open, onToggle, onSelect, onNotice }) {
  const { t } = useTranslation('mediaStudio');
  const customFonts = useCustomFonts();
  const brandFonts = useBrandFonts();
  const [query, setQuery] = useState('');
  const fileRef = useRef(null);

  const sections = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (font) => !q || font.label.toLowerCase().includes(q);
    return [
      { id: 'brand', label: t('brandFonts'), fonts: brandFonts.filter(match) },
      { id: 'custom', label: t('myFonts'), fonts: customFonts.filter(match) },
      { id: 'system', label: t('fonts'), fonts: SYSTEM_FONTS.filter(match) },
    ].filter((section) => section.fonts.length);
  }, [brandFonts, customFonts, query, t]);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    try {
      const font = await addCustomFont(file);
      onSelect(font.id);
    } catch {
      onNotice?.(t('fontUploadFailed'));
    }
  };

  return (
    <div className={scss.menuAnchor}>
      <button
        type='button'
        className={scss.fontButton}
        aria-expanded={open}
        title={t('fontFamily')}
        onMouseDown={(event) => event.preventDefault()}
        onClick={onToggle}>
        <span style={value ? { fontFamily: getFontStack(value) } : undefined}>
          {value ? getFontLabel(value) : t('mixed')}
        </span>
        <ChevronDown size={14} />
      </button>

      {open ? (
        <div className={clsx(scss.menu, scss.fontMenu)}>
          <input
            type='search'
            className={scss.search}
            placeholder={t('fontSearch')}
            aria-label={t('fontSearch')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => event.stopPropagation()}
          />
          <div className={scss.fontList}>
            {sections.length ? (
              sections.map((section) => (
                <div key={section.id}>
                  <p className={scss.menuLabel}>{section.label}</p>
                  {section.fonts.map((font) => (
                    <button
                      key={font.id}
                      type='button'
                      className={clsx(scss.fontOption, font.id === value && scss.fontOptionActive)}
                      style={{ fontFamily: font.stack }}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => onSelect(font.id)}>
                      <span>{font.label}</span>
                      {font.id === value ? <Check size={14} /> : null}
                    </button>
                  ))}
                </div>
              ))
            ) : (
              <p className={scss.menuEmpty}>{t('fontSearchEmpty')}</p>
            )}
          </div>
          <button
            type='button'
            className={scss.menuAction}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => fileRef.current?.click()}>
            <Upload size={14} />
            {t('uploadFont')}
          </button>
          <input ref={fileRef} type='file' accept={FONT_FILE_ACCEPT} hidden onChange={handleUpload} />
        </div>
      ) : null}
    </div>
  );
}

export default FontPicker;
